import type { ApiEnvelope } from '@/api/errors';
import { filenameFromContentDisposition, triggerBlobDownload } from '@/lib/download';
import type {
  AddressOption,
  BargainCreateStatus,
  BuyTreeResponse,
  ConsigneeDetails,
  CreateDealFromSaleRequest,
  EditPriceListRequest,
  GoodsPropertiesSet,
  OrderFile,
  PurchasePriceList,
  QualityAcceptanceProperty,
  SaleOrderView,
} from '@/types/buy';
import type { CreateDealFromPurchaseRequest, PurchaseOrderView, SellDealParams } from '@/types/sellDeal';
import { BaseService } from './BaseService';

// Write-операции order-service на dev регулярно дольше дефолтных 15с (Camunda/нотификации) — даём 60с.
const WRITE_TIMEOUT_MS = 60_000;

// Поля, которых не хватает пользователю для работы (для баннера обязательных данных).
export type RequiredField =
  | 'BANK_REQUISITS'
  | 'CONTRAGENT_BANK_REQUISITS'
  | 'CONTACT_PHONE'
  | 'CONTRAGENT_CONTACT_PHONE'
  | 'ADDRESS'
  | 'CONTRAGENT_ADDRESS'
  | 'MANAGEMENT_NAME'
  | 'MANAGEMENT_POST'
  | 'TAX_SYSTEM'
  | 'CERTIFICATION'
  | 'RESEND_CERTIFICATION';

export interface RequiredData {
  readonly requiredUserDataFields: RequiredField[];
  readonly requiredBargainsToPay: number[];
  readonly otherMessages: string[];
}

export interface PurchasePriceListParams {
  readonly address: string;
  readonly addressTitle: string;
  readonly parentGoodsType: string;
  /** Год урожая — только от сервера выпуска А (узел дерева); у закупки года нет (решение 2026-11-04). */
  readonly cropYear?: number;
  /** false — вернуть и пустые (шаблонные) строки качества; по умолчанию true (только мои с ценой). */
  readonly onlyExisted?: boolean;
}

// Сервис order-service: заявки/сделки/документы. Конверт стандартный { success, data } (сверено вживую).
/** Регион: `numberId` — официальный код (23, 61, …), он же хранится в заявках. Состав списка зависит от
 * источника: `getRegionDictionary` отдаёт справочник целиком, `/order/all_regions` — только регионы торговли. */
export interface RegionOption {
  readonly numberId: number;
  readonly name: string;
}

class OrderService extends BaseService {
  /** Статус обязательных данных (баннер). GET — идемпотентно; при пустом ответе баннер скрыт. */
  async getRequiredData(): Promise<RequiredData> {
    const response = await this.request<ApiEnvelope<RequiredData>>(
      { method: 'get', url: '/order/get_banners_data' },
      { idempotent: true },
    );
    const data = response.data?.data;
    return {
      requiredUserDataFields: data?.requiredUserDataFields ?? [],
      requiredBargainsToPay: data?.requiredBargainsToPay ?? [],
      otherMessages: data?.otherMessages ?? [],
    };
  }

  // ── «Купить»: мои прайс-листы на покупку ──────────────────────────────────────
  /** Дерево моих закупок (базисы → товары с годом). Источник списка и фильтров на /app/buy. */
  async getBuyTree(signal?: AbortSignal): Promise<BuyTreeResponse> {
    const response = await this.request<ApiEnvelope<BuyTreeResponse>>(
      { method: 'get', url: '/order/tree/buy_cpt' },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Детальный прайс-лист группы (базис + товар + год): группы товаров со строками качества и ценами. */
  async getPurchasePriceList(
    params: PurchasePriceListParams,
    signal?: AbortSignal,
  ): Promise<PurchasePriceList> {
    const response = await this.request<ApiEnvelope<PurchasePriceList>>(
      {
        method: 'get',
        url: '/order/my/purchase/prices_list',
        params: { ...params, onlyExisted: params.onlyExisted ?? true },
      },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  // ── Действия с прайс-листами (Фаза 3) ─────────────────────────────────────────
  /**
   * Массовый сдвиг цен группы (культура на базисе): delta — знаковая, ₽/т; бэкенд применяет её ко ВСЕМ
   * строкам качества и обоим типам предоплаты (новая = нормализованная(старая + delta)).
   */
  async updatePurchasePrices(params: {
    address: string;
    addressTitle: string;
    parentGoodsType: string;
    cropYear?: number;
    delta: number;
  }): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/order/my/purchase/update_prices', data: params },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Удалить группу прайс-листа (все строки культуры на базисе); год — только если сервер его прислал (выпуск А). */
  async deletePurchaseGroup(params: {
    address: string;
    addressTitle: string;
    parentGoodsType: string;
    year?: number;
  }): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'delete', url: '/order/my/purchase/orders', params },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Удалить ВСЕ прайс-листы базиса (все товары на адресе). */
  async deletePurchaseByBasis(params: { address: string; addressTitle: string }): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'delete', url: '/order/my/purchase/orders/by_basis', params },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Форма прайс-листа (Фаза 3) ────────────────────────────────────────────────
  /** Автокомплит адреса точки выгрузки. */
  async searchAddress(query: string, signal?: AbortSignal): Promise<AddressOption[]> {
    const response = await this.request<ApiEnvelope<{ addressList?: AddressOption[] }>>(
      { method: 'get', url: '/order/address/search', params: { address: query, limit: 20 } },
      { idempotent: true, signal },
    );
    return response.data?.data?.addressList ?? [];
  }

  /** Словарь родительских культур («Пшеница», «Ячмень», …). */
  async getParentGoodsTypes(signal?: AbortSignal): Promise<string[]> {
    const response = await this.request<ApiEnvelope<string[]>>(
      { method: 'get', url: '/order/goods_types/parent' },
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  /**
   * Полный справочник регионов: код и название («23 — Краснодарский край»). Код тот же, что заявка на
   * перевозку хранит в полях регионов, поэтому справочник и служит для их подписи.
   *
   * <p>Берём именно справочник, а не соседний `/order/all_regions`: тот урезан перечнем регионов
   * торговли (сейчас 15 записей), а регион заявки определяется адресом и вполне может лежать вне его —
   * тогда экран показал бы вместо названия голое число.
   */
  async getRegionDictionary(signal?: AbortSignal): Promise<RegionOption[]> {
    const response = await this.request<ApiEnvelope<RegionOption[]>>(
      { method: 'get', url: '/order/geo_regions' },
      { idempotent: true, signal },
    );
    // Логическая ошибка приходит с кодом 200 — без проверки конверта справочник молча стал бы пустым.
    this.ensureSuccess(response.data, response.status);
    return response.data?.data ?? [];
  }

  /** Словарь культур с разделением на классы («Пшеница 3 класс», …) — для поиска лучшей цены. */
  async getGoodsClasses(signal?: AbortSignal): Promise<string[]> {
    const response = await this.request<ApiEnvelope<string[]>>(
      { method: 'get', url: '/order/goods_types/class_separated' },
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  /** Варианты качества культуры (наборы свойств с propsSetId) — для фильтра поиска. */
  async getGoodsPropertySets(goodsType: string, signal?: AbortSignal): Promise<GoodsPropertiesSet[]> {
    const response = await this.request<ApiEnvelope<GoodsPropertiesSet[]>>(
      { method: 'get', url: '/order/goods_propersties', params: { goodsType } },
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  /** Дефолтный грузополучатель прайса на адресе (по умолчанию — сам покупатель). */
  async getDefaultConsignee(address: string, title: string, signal?: AbortSignal): Promise<ConsigneeDetails> {
    const response = await this.request<ApiEnvelope<ConsigneeDetails>>(
      { method: 'get', url: '/order/purchase/default_consignee', params: { address, title } },
      { idempotent: true, signal },
    );
    return response.data?.data ?? {};
  }

  /** Загрузка файлов особых условий покупки (multipart) → дескрипторы файлов. */
  async uploadPurchaseSpecialTerms(files: File[]): Promise<OrderFile[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    const response = await this.request<ApiEnvelope<{ files?: OrderFile[] }>>(
      { method: 'post', url: '/order/purchase/upload/special_terms', data: formData },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    return this.unwrap(response.data, response.status).files ?? [];
  }

  /** Скачать файл заявки (blob → файл; имя — из Content-Disposition, как в Аттестации). */
  async downloadOrderFile(fileId: number): Promise<void> {
    const response = await this.request<Blob>(
      { method: 'get', url: '/order/file', params: { fileId }, responseType: 'blob' },
      { idempotent: true },
    );
    const filename =
      filenameFromContentDisposition(response.headers['content-disposition'] as string | undefined) ??
      'document';
    triggerBlobDownload(response.data, filename);
  }

  /** Удалить загруженный (ещё не привязанный) файл покупки. */
  async deletePurchaseFile(fileId: number): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'delete', url: '/order/purchase/file', params: { fileId } },
      { retry: false },
    );
    this.ensureSuccess(response.data, response.status);
  }

  /** Создание/обновление прайс-листа покупки (строки качества — серверные, клиент шлёт цены). */
  async editPriceList(body: EditPriceListRequest): Promise<void> {
    const response = await this.request<ApiEnvelope<unknown>>(
      { method: 'post', url: '/order/purchase/edit/prices_list', data: body },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    this.ensureSuccess(response.data, response.status);
  }

  // ── Покупка по лучшей цене (Фаза 2b) ──────────────────────────────────────────
  /** Превью оффера продажи для покупателя (CPT-цены с доставкой до toAddress). */
  async getSaleOrderView(
    params: {
      orderId: number;
      orderVersion: number;
      fromPurchaseOrderId?: number;
      toAddress?: string;
      toAddressTitle?: string;
    },
    signal?: AbortSignal,
  ): Promise<SaleOrderView> {
    const response = await this.request<ApiEnvelope<SaleOrderView>>(
      { method: 'get', url: '/order/sale/by_id', params: { ...params, bargainType: 'FCA_TEO' } },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** ГОСТ-дефолты приёмки по родительскому товару (для qualityAcceptance сделки, как старый портал). */
  async getQaDefaults(parentGoodsType: string, signal?: AbortSignal): Promise<QualityAcceptanceProperty[]> {
    const response = await this.request<ApiEnvelope<QualityAcceptanceProperty[]>>(
      { method: 'get', url: '/order/purchase/qa_props/default', params: { goodsType: parentGoodsType } },
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  /** Создание сделки из оффера продажи (Camunda, асинхронно) → processId для поллинга статуса. */
  async createDealFromSale(body: CreateDealFromSaleRequest): Promise<string> {
    const response = await this.request<ApiEnvelope<{ camundaProcessId?: string }>>(
      { method: 'post', url: '/order/bargain/create/sale_order/agent_cpt', data: body },
      // Старт Camunda-процесса на dev может занимать >15с (дефолтный таймаут) — даём 60с.
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    const processId = this.unwrap(response.data, response.status).camundaProcessId;
    if (!processId) {
      throw new Error('Bargain creation response has no process id');
    }
    return processId;
  }

  /**
   * Превью встречной закупки покупателя для продавца: FCA-цена у ворот по моему базису (== best-price
   * виджета) + CPT-цена покупателя. fromAddress/fromAddressTitle — мой базис погрузки. Оборудование
   * (hasScales/hasTrawl/hasTrailer) передаём в свободном режиме (без заявки) — по нему сервер авторитетно
   * считает логистику и FCA; при отсутствии параметров сервер берёт оборудование из связанной заявки.
   */
  async getPurchaseOrderView(
    params: {
      orderId: number;
      orderVersion: number;
      fromSaleOrderId?: number;
      fromAddress?: string;
      fromAddressTitle?: string;
      hasScales?: boolean;
      hasTrawl?: boolean;
      hasTrailer?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<PurchaseOrderView> {
    const response = await this.request<ApiEnvelope<PurchaseOrderView>>(
      { method: 'get', url: '/order/purchase/by_id', params: { ...params, bargainType: 'AGENT_CPT' } },
      { idempotent: true, signal },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Сгенерировать поручение агенту по сделке продажи (по встречной закупке orderId) → файл поручения. */
  async generateBargainAgentAssignment(orderId: number, body: SellDealParams): Promise<OrderFile> {
    const response = await this.request<ApiEnvelope<OrderFile>>(
      { method: 'post', url: `/order/bargain/generate/agent_assignment/${orderId}`, data: { ...body, bargainType: 'AGENT_CPT' } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    return this.unwrap(response.data, response.status);
  }

  /** Подписать поручение агенту по сделке (синхронно, без тела). */
  async signBargainAgentAssignment(fileId: number): Promise<void> {
    await this.request<ApiEnvelope<void>>(
      { method: 'post', url: `/order/bargain/agent_assignment/${fileId}/sign` },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
  }

  /** Создание сделки из встречной закупки покупателя (Camunda, асинхронно) → processId для поллинга.
   *  Со встречным офером (fromSaleOrderId) бэкенд определяет логистику и адрес по заявке и принимает запрос
   *  ТОЛЬКО на `…/agent_cpt/from_sale`; на плоском `…/agent_cpt` он отвечает «fromSaleOrderId is not
   *  supported» (как в старом портале: createPurchaseOrder vs createPurchaseOrderFromSale). */
  async createDealFromPurchase(body: CreateDealFromPurchaseRequest): Promise<string> {
    const url =
      body.fromSaleOrderId != null
        ? '/order/bargain/create/purchase_order/agent_cpt/from_sale'
        : '/order/bargain/create/purchase_order/agent_cpt';
    const response = await this.request<ApiEnvelope<{ camundaProcessId?: string }>>(
      { method: 'post', url, data: { ...body, bargainType: 'AGENT_CPT' } },
      { retry: false, timeoutMs: WRITE_TIMEOUT_MS },
    );
    const processId = this.unwrap(response.data, response.status).camundaProcessId;
    if (!processId) {
      throw new Error('Bargain creation response has no process id');
    }
    return processId;
  }

  /** Статус асинхронного создания сделки (поллинг). */
  async getBargainCreateStatus(processId: string, signal?: AbortSignal): Promise<BargainCreateStatus> {
    const response = await this.request<ApiEnvelope<BargainCreateStatus>>(
      {
        method: 'get',
        url: `/order/bargain/create/${encodeURIComponent(processId)}/status`,
      },
      { idempotent: true, signal },
    );
    return response.data?.data ?? {};
  }
}

export const orderService = new OrderService();
